import React from "react";
import {
    Box,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import {
    CheckCircle as CheckIcon,
    PictureAsPdf as PdfIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import AdSense from "../../components/AdSense";
import SEO from "../../components/SEO/SEO";
import PageHero from "../../components/PageHero/PageHero";
import AppDownloadCTA, {
    PageShell,
} from "../../components/AppDownloadCTA/AppDownloadCTA";
import { PhoneMockup } from "../../components/ScreenshotGallery/ScreenshotGallery";
import { pageMeta } from "../../seo/pageMeta";
import { APP_SCREENSHOTS } from "../../seo/assets";
import { SITE_URL } from "../../seo/constants";

const ARABIC_PDF =
    "https://firebasestorage.googleapis.com/v0/b/quran-chat-buddy-prayer-time.firebasestorage.app/o/quran-pdfs%2F15-Line-Standard-Quran-PDF.pdf?alt=media&token=70e26558-7148-4f63-b118-6947c8c8690a";
const ENGLISH_PDF =
    "https://firebasestorage.googleapis.com/v0/b/quran-chat-buddy-prayer-time.firebasestorage.app/o/quran-pdfs%2FHoly-Quran-English.pdf?alt=media&token=1a8ce865-aacf-4ef9-9f67-7ff6f82482ca";

const reasons = [
    "Read the Holy Quran offline without internet",
    "Ideal for travel, work, school, or quiet study",
    "Arabic mushaf and English translation for Muslims worldwide",
    "Helpful for Quran memorization (Hifz) and revision",
    "Keep a personal Quran PDF on phone, tablet, or desktop",
    "Pair with Quran Chat Buddy for tafsir, Salah times, and AI guidance",
];

export default function QuranPdf() {
    const meta = pageMeta.quranPdf;

    return (
        <PageShell maxWidth="md">
            <SEO
                {...meta}
                image={`${SITE_URL}/images/screenshots/screen-4.webp`}
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "WebPage",
                    name: meta.title,
                    description: meta.description,
                    url: `${SITE_URL}${meta.path}`,
                }}
            />

            <PageHero
                eyebrow="Free Holy Quran PDF"
                title="Quran PDF Download — Arabic & English"
                subtitle="Download the Holy Quran PDF free for offline reading. Choose a clear Arabic mushaf or an English translation — perfect for Muslims seeking Quran study, daily recitation, and spiritual growth."
                breadcrumbs={[
                    { label: "Home", to: "/" },
                    { label: "Quran PDF Download" },
                ]}
            />

            <Grid container spacing={3} sx={{ mb: 4 }} alignItems="center">
                <Grid item xs={12} md={7}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Card sx={{ height: "100%", textAlign: "center" }}>
                                <CardContent sx={{ p: 3 }}>
                                    <PdfIcon
                                        sx={{ fontSize: 40, color: "primary.main", mb: 1 }}
                                    />
                                    <Typography variant="h5" component="h2" sx={{ mb: 1.5 }}>
                                        Arabic Quran PDF
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ mb: 2.5, color: "text.secondary" }}
                                    >
                                        Standard 15-line Arabic Quran PDF for offline
                                        reading, memorization, and daily tilawah.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        href={ARABIC_PDF}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        fullWidth
                                    >
                                        Download Arabic Quran
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Card sx={{ height: "100%", textAlign: "center" }}>
                                <CardContent sx={{ p: 3 }}>
                                    <PdfIcon
                                        sx={{ fontSize: 40, color: "secondary.main", mb: 1 }}
                                    />
                                    <Typography variant="h5" component="h2" sx={{ mb: 1.5 }}>
                                        English Quran PDF
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ mb: 2.5, color: "text.secondary" }}
                                    >
                                        Holy Quran with English translation — understand
                                        meaning while you read and reflect.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        href={ENGLISH_PDF}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        fullWidth
                                    >
                                        Download English Quran
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} md={5}>
                    <PhoneMockup
                        src={APP_SCREENSHOTS[3].src}
                        alt="Quran learning in Quran Chat Buddy app"
                        maxWidth={220}
                    />
                </Grid>
            </Grid>

            <Box sx={{ my: 4 }}>
                <AdSense slot="2566597353" />
            </Box>

            <Typography variant="h4" component="h2" sx={{ mb: 2, fontWeight: 700 }}>
                Why Download a Quran PDF?
            </Typography>
            <List>
                {reasons.map((item) => (
                    <ListItem key={item} sx={{ py: 0.5, alignItems: "flex-start" }}>
                        <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                            <CheckIcon color="primary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                            primary={item}
                            primaryTypographyProps={{ sx: { lineHeight: 1.7 } }}
                        />
                    </ListItem>
                ))}
            </List>

            <Typography variant="h4" component="h2" sx={{ mt: 4, mb: 2, fontWeight: 700 }}>
                Quran PDF for Muslims — Study Anytime
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                Millions of Muslims search for a free Quran PDF to read the Holy Quran
                offline. Whether you want an Arabic mushaf, English translation, or a
                portable file for Surah study, this page gives you direct downloads you
                can keep on any device.
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                After downloading, continue with{" "}
                <Box component={Link} to="/surah-yaseen" sx={{ color: "primary.main" }}>
                    Surah Yaseen PDF
                </Box>
                , explore our{" "}
                <Box component={Link} to="/blogs/quran" sx={{ color: "primary.main" }}>
                    Quran learning guide
                </Box>
                , or open the Quran Chat Buddy app for AI tafsir, Salah prayer times,
                Qibla, and daily ayat reminders.
            </Typography>

            <AppDownloadCTA
                title="Read Quran smarter with Quran Chat Buddy"
                subtitle="Pair your Quran PDF with AI answers, prayer times, Qibla direction, and daily Islamic reminders — free for iPhone and Android."
            />
        </PageShell>
    );
}
